import React from 'react';
import Text from '../Text';
import type { TextProps } from '../Text/Text';

export interface HighlightTextProps extends TextProps {
  /** The text to display */
  children: string;
  /** The search term(s) to highlight. Can be a string or array of strings */
  highlight?: string | string[];
  /** Background color for highlighted text */
  highlightColor?: string;
  /** Inline styles (not part of TextProps but passed through to Text) */
  style?: React.CSSProperties;
}

/**
 * Splits text into parts based on search terms and returns segments with highlight flags.
 * Case-insensitive matching.
 */
function getHighlightedParts(
  text: string,
  searchTerms: string[],
): Array<{ text: string; isHighlighted: boolean }> {
  const validTerms = searchTerms.filter((term) => term.length > 0);

  if (validTerms.length === 0) {
    return [{ text, isHighlighted: false }];
  }

  // Escape regex special characters for pattern matching
  const escapedTerms = validTerms.map((term) =>
    term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  );

  // Create regex pattern that matches any of the search terms (case-insensitive)
  const pattern = new RegExp(`(${escapedTerms.join('|')})`, 'gi');
  const parts = text.split(pattern);

  return parts
    .filter((part) => part !== '')
    .map((part) => ({
      text: part,
      isHighlighted: validTerms.some(
        (term) => part.toLowerCase() === term.toLowerCase(),
      ),
    }));
}

/**
 * Text component that highlights matching search terms.
 * Useful for showing which parts of text matched a filter/search query.
 */
export function HighlightText({
  children,
  highlight,
  highlightColor = '#ffe552',
  ...textProps
}: HighlightTextProps): React.ReactElement {
  const searchTerms = React.useMemo(() => {
    if (!highlight) return [];
    return Array.isArray(highlight) ? highlight : [highlight];
  }, [highlight]);

  const parts = React.useMemo(
    () => getHighlightedParts(children, searchTerms),
    [children, searchTerms],
  );

  // If no highlighting needed, render simple text
  if (parts.length === 1 && !parts[0].isHighlighted) {
    return <Text {...textProps}>{children}</Text>;
  }

  return (
    <Text {...textProps}>
      {parts.map((part, index) =>
        part.isHighlighted ? (
          <mark
            key={index}
            style={{
              backgroundColor: highlightColor,
              padding: 0,
              borderRadius: 2,
            }}
          >
            {part.text}
          </mark>
        ) : (
          <React.Fragment key={index}>{part.text}</React.Fragment>
        ),
      )}
    </Text>
  );
}

export default HighlightText;
